"use client";

import {
  type AnchorHTMLAttributes,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  type CommercialInteraction,
  trackCommercialClick,
  trackCommercialImpression,
} from "@/lib/commercial-analytics";

const VISIBILITY_THRESHOLD = 0.5;
const VISIBILITY_DURATION_MS = 700;

function useMeaningfulCommercialImpression(
  ref: RefObject<HTMLElement | null>,
  interaction: CommercialInteraction | null,
) {
  useEffect(() => {
    const element = ref.current;
    if (!element || !interaction || typeof IntersectionObserver === "undefined") return;
    let timer: number | null = null;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD);
      if (visible && timer === null) {
        timer = window.setTimeout(() => {
          timer = null;
          trackCommercialImpression(interaction);
          observer.disconnect();
        }, VISIBILITY_DURATION_MS);
      } else if (!visible && timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }, { threshold: [VISIBILITY_THRESHOLD] });
    observer.observe(element);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [interaction, ref]);
}

export function CommercialTrackedArticle({
  interaction,
  children,
  ...props
}: ComponentPropsWithoutRef<"article"> & {
  interaction: CommercialInteraction | null;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const signature = interaction ? JSON.stringify(interaction) : "";
  // Public cards often rerender as filters/carousels move. Keep the observer
  // alive when the commercial identity and context have not changed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableInteraction = useMemo(() => interaction, [signature]);
  useMeaningfulCommercialImpression(ref, stableInteraction);
  return <article ref={ref} {...props}>{children}</article>;
}

export function CommercialTrackedLink({
  interaction,
  children,
  onClick,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  interaction: CommercialInteraction | null;
  children: ReactNode;
}) {
  const clicked = (event: MouseEvent<HTMLAnchorElement>) => {
    if (interaction) trackCommercialClick(interaction, props.href);
    onClick?.(event);
  };
  return <a {...props} onClick={clicked}>{children}</a>;
}

export const commercialVisibilityRule = {
  threshold: VISIBILITY_THRESHOLD,
  durationMs: VISIBILITY_DURATION_MS,
} as const;
